import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Snackbar } from './Snackbar';
export const SnackbarContext = React.createContext({
    triggerSnackbar: () => {
        /* noOp */
    },
    show: false,
});
const SnackbarProvider = ({ children }) => {
    const [show, setShow] = useState(false);
    const [queue, setQueue] = useState([]);
    const [portal, setPortal] = useState();
    const [queueIsActive, setQueueIsActive] = useState(false);
    const contentRef = useRef(null);
    const triggerSnackbar = (triggerOptions) => {
        setQueue([...queue, Object.assign({}, triggerOptions)]);
    };
    const timerIdRef = useRef(undefined);
    const updateQueue = useCallback((firstQueueItem) => {
        setQueueIsActive(true);
        /** We keep track of whether the button in the current snackbar
         * has been clicked or not because we want to avoid running the `onBlur`
         * handler and consequently running the logic for hiding the
         * snackbar a second time. */
        let buttonClicked = false;
        const { timeLimit = 4, onClose, columnSize, text, snackbarButtonOnClick, snackbarButtonText, } = firstQueueItem;
        const targetNode = document.activeElement;
        const divWrapper = document.createElement('div');
        divWrapper.style.display = 'inline';
        divWrapper.dataset.testid = 'mui-snackbar-portal';
        targetNode === null || targetNode === void 0 ? void 0 : targetNode.insertAdjacentElement('afterend', divWrapper);
        let calledOnClose = false;
        const hideTimer = (time) => {
            return window.setTimeout(() => {
                timerIdRef.current = window.setTimeout(() => {
                    divWrapper.remove();
                    buttonClicked = false;
                    if (!buttonClicked && !calledOnClose)
                        onClose === null || onClose === void 0 ? void 0 : onClose();
                    setQueue(prevQueue => prevQueue.slice(1));
                    setQueueIsActive(false);
                }, 250);
                setShow(false);
                targetNode === null || targetNode === void 0 ? void 0 : targetNode.focus();
            }, time * 1000);
        };
        timerIdRef.current = window.setTimeout(() => {
            var _a;
            timerIdRef.current = hideTimer(timeLimit);
            setShow(true);
            (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }, 100);
        const snackbar = (React.createElement(Snackbar, { ref: contentRef, text: text, columnSize: columnSize, snackbarButtonText: snackbarButtonText, snackbarButtonOnClick: (e) => {
                if (!buttonClicked) {
                    setShow(false);
                    targetNode.focus();
                    divWrapper.remove();
                    clearTimeout(timerIdRef.current);
                    buttonClicked = true;
                    snackbarButtonOnClick === null || snackbarButtonOnClick === void 0 ? void 0 : snackbarButtonOnClick(e);
                    onClose === null || onClose === void 0 ? void 0 : onClose();
                    calledOnClose = true;
                    setQueue(prevQueue => prevQueue.slice(1));
                    setQueueIsActive(false);
                }
            }, buttonOnFocus: () => {
                if (timerIdRef.current != null)
                    clearTimeout(timerIdRef.current);
            }, buttonOnBlur: () => {
                if (!buttonClicked) {
                    timerIdRef.current = hideTimer(timeLimit);
                }
            } }));
        setPortal(createPortal(snackbar, divWrapper));
    }, []);
    useEffect(() => {
        return () => clearTimeout(timerIdRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (queue.length && !queueIsActive) {
            updateQueue(queue[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queue, queueIsActive]);
    return (React.createElement(SnackbarContext.Provider, { value: { triggerSnackbar, show } },
        children,
        portal));
};
export { SnackbarProvider };
//# sourceMappingURL=SnackbarProvider.js.map